import {
    ColumnDef,
    ColumnOrderState,
    ColumnSizingInfoState,
    ColumnSizingState,
    OnChangeFn,
    PaginationState,
    Row,
    RowSelectionState,
    SortingState,
    VisibilityState,
    functionalUpdate,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { resetTableCheckbox, setCheckedTableRows } from '../../../store/table/actions'
import { selectCheckboxValue } from '../../../store/table/selectors'
import getCheckboxColumn from '../utils/checkboxColumn'

interface SelectRowConfig<TData> {
    mapRow: (row: TData) => unknown
    enableRow?: (row: TData) => boolean
}

interface UseDataTableConfig<TData> {
    columns: ColumnDef<TData>[]
    data: TData[]
    pageCount?: number
    pagination: PaginationState
    onPaginationChange: OnChangeFn<PaginationState>
    sorting: SortingState
    onSortingChange: OnChangeFn<SortingState>
    columnVisibility: VisibilityState
    onColumnVisibilityChange: OnChangeFn<VisibilityState>
    columnOrder: ColumnOrderState
    onColumnOrderChange: OnChangeFn<ColumnOrderState>
    columnSizing: ColumnSizingState
    onColumnSizingChange: OnChangeFn<ColumnSizingState>
    onColumnSizingInfoChange?: OnChangeFn<ColumnSizingInfoState>
    updateColumnSizing: (sizing: ColumnSizingState) => void
    selectRow?: SelectRowConfig<TData>
    tableKey?: string
    params?: unknown
}

const useDataTable = <TData>({
    columns,
    data,
    pageCount,
    pagination,
    onPaginationChange,
    sorting,
    onSortingChange,
    columnVisibility,
    onColumnVisibilityChange,
    columnOrder,
    onColumnOrderChange,
    columnSizing,
    onColumnSizingChange,
    onColumnSizingInfoChange,
    updateColumnSizing,
    selectRow,
}: UseDataTableConfig<TData>) => {
    const dispatch = useDispatch()
    const checkboxReset = useSelector(selectCheckboxValue)
    const [rowSelection, setRowSelection] = useState<RowSelectionState>({})

    // Stale-page guard: when a filter (pageSize, search) reduces totalPages below the current page, reset to first
    useEffect(() => {
        if (pageCount !== undefined && pageCount > 0 && pagination.pageIndex >= pageCount) {
            onPaginationChange((prev) => ({ ...prev, pageIndex: 0 }))
        }
    }, [pageCount, pagination.pageIndex, onPaginationChange])

    // Reset row selection when a bulk action completes
    useEffect(() => {
        if (checkboxReset && selectRow) {
            setRowSelection({})
            dispatch(resetTableCheckbox(false))
        }
    }, [checkboxReset, selectRow, dispatch])

    const onRowSelectionChange: OnChangeFn<RowSelectionState> = (updater) => {
        if (!selectRow) return

        const newSelectionState = functionalUpdate(updater, rowSelection)

        setRowSelection(newSelectionState)

        const selected = table
            .getCoreRowModel()
            .rows.filter((r) => newSelectionState[r.id])
            .map((r) => selectRow.mapRow(r.original))

        dispatch(setCheckedTableRows(selected))
    }

    const allColumns: ColumnDef<TData>[] = selectRow ? [getCheckboxColumn<TData>(), ...columns] : columns
    const hasActions = columns.some((c) => c.id === 'actions')

    const table = useReactTable({
        columns: allColumns,
        data,
        defaultColumn: { minSize: 60 },
        columnResizeMode: 'onChange',
        enableColumnResizing: true,
        manualSorting: true,
        enableSortingRemoval: false,
        autoResetPageIndex: false,
        getCoreRowModel: getCoreRowModel(),
        onSortingChange,
        onColumnVisibilityChange,
        onColumnOrderChange,
        onColumnSizingChange,
        onColumnSizingInfoChange,
        manualPagination: true,
        pageCount,
        onPaginationChange,
        ...(selectRow && {
            enableRowSelection: (row: Row<TData>) => selectRow.enableRow?.(row.original) ?? true,
            onRowSelectionChange,
        }),
        initialState: {
            columnPinning: {
                left: selectRow ? ['checkbox'] : [],
                right: hasActions ? ['actions'] : [],
            },
        },
        state: {
            sorting,
            pagination,
            columnVisibility,
            columnOrder,
            columnSizing,
            ...(selectRow && { rowSelection }),
        },
    })

    return { table, updateColumnSizing }
}

export default useDataTable
